import React, { useEffect, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import axios from 'axios';
import '../styles/contact-section.css';

const TOPIC_OPTIONS = [
  { value: 'free-trial', label: 'Free Trial' },
  { value: 'contact-sales', label: 'Contact Sales' },
  { value: 'support', label: 'Technical Support' },
  { value: 'report-bug', label: 'Report a Bug' },
  { value: 'general-inquiry', label: 'General Inquiry' },
];

const TOPIC_VALUES = new Set(TOPIC_OPTIONS.map((option) => option.value));

const normalizeTopic = (value) => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-')
    .replace(/\s+/g, '-');
  return TOPIC_VALUES.has(normalized) ? normalized : 'general-inquiry';
};

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    topic: 'general-inquiry',
    message: '',
    agreed: false,
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const topic = normalizeTopic(params.get('topic'));
    const plan = (params.get('plan') || '').trim();
    const source = (params.get('source') || '').trim().toLowerCase();

    let prefillMessage = '';
    if (source === 'pricing' && plan) {
      prefillMessage = `Please share pricing details for the ${plan} plan.`;
    } else if (source === 'pricing-trial' && plan) {
      prefillMessage = `I would like to start a free trial for the ${plan} plan.`;
    }

    setFormData((prev) => ({
      ...prev,
      topic,
      message: prev.message || prefillMessage,
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await axios.post('/api/contact', formData);
      if (response.status === 200) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            company: '',
            topic: 'general-inquiry',
            message: '',
            agreed: false,
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError(
        error.response?.data?.error || 'There was an error submitting your message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section" data-testid="contact-section">
      <div className="content-container">
        <div className="left-content">
          <p className="contact-eyebrow">Contact us</p>
          <h2 className="contact-title">Plan your ADX Hub onboarding with our team</h2>
        </div>

        <div className="right-content">
          {isSubmitted ? (
            <div className="contact-success">
              <div className="contact-success-icon">
                <Check className="h-8 w-8" strokeWidth={2.2} />
              </div>
              <h3>Message sent</h3>
              <p>We will get back to you shortly.</p>
            </div>
          ) : (
            <form id="contact-form" className="form-fields" onSubmit={handleSubmit}>
              <div className="form-field-row">
                <div className="form-field-group">
                  <label htmlFor="contact-name" className="contact-field-label">Name</label>
                  <input
                    id="contact-name"
                    type="text"
                    className="contact-input"
                    name="name"
                    placeholder="Enter your name *"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="input-name"
                  />
                </div>
                <div className="form-field-group">
                  <label htmlFor="contact-email" className="contact-field-label">Email</label>
                  <input
                    id="contact-email"
                    type="email"
                    className="contact-input"
                    name="email"
                    placeholder="Enter your email address *"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>
              <div className="form-field-group">
                <label htmlFor="contact-company" className="contact-field-label">Organization</label>
                <input
                  id="contact-company"
                  type="text"
                  className="contact-input"
                  name="company"
                  placeholder="Enter your organization"
                  value={formData.company}
                  onChange={handleChange}
                  data-testid="input-company"
                />
              </div>
              <div className="form-field-group">
                <label htmlFor="contact-topic" className="contact-field-label">Topic</label>
                <select
                  id="contact-topic"
                  className="contact-input"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  required
                  data-testid="input-topic"
                >
                  {TOPIC_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field-group">
                <label htmlFor="contact-message" className="contact-field-label">Message</label>
                <textarea
                  id="contact-message"
                  className="contact-textarea"
                  name="message"
                  placeholder="Tell us about your digital transformation goals... *"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  data-testid="input-message"
                />
              </div>
              <div className="consent-section">
                <input
                  type="checkbox"
                  className="consent-checkbox"
                  name="agreed"
                  id="consent"
                  checked={formData.agreed}
                  onChange={handleChange}
                  required
                  data-testid="input-agreed"
                />
                <label htmlFor="consent" className="consent-label">
                  <span className="consent-text">
                    I agree that my personal data provided will be processed and stored for communication in accordance with the{' '}
                    <a href="https://industrialdigitaltwin.org/en/privacy" target="_blank" rel="noopener noreferrer">
                      privacy policy
                    </a>
                    .
                  </span>
                </label>
              </div>
              {submitError ? (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" role="alert">
                  {submitError}
                </p>
              ) : null}
              <button type="submit" className="contact-submit-btn" disabled={isSubmitting} data-testid="submit-btn">
                {isSubmitting ? 'Sending…' : 'Submit'}
                <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
